import {ReactNode, useEffect, useRef, useState} from "react";
import {CheckIcon, CircleAlertIcon, Loader2Icon, PlusIcon, UserRoundIcon} from "lucide-react";
import {SearchResult} from "@/api/SearchApi.ts";
import {srcUrl} from "@/api/ImageApi.ts";
import {cn} from "@/lib/utils.ts";
import {Button} from "@/components/ui/button.tsx";
import {useModal} from "@/modal/ModalHooks.ts";
import {addCharacterToEncounter} from "@/api/CombatantApi.ts";
import {formatChallengeRating, getStyleColor} from "@/characters/model/ChallengeRating.ts";
import {useActiveEncounter} from "@/encounter/ActiveEncounterHooks.ts";
import {HighlightedSearchText} from "@/search/SearchPopover.tsx";
import SpellSearchResultEntry from "@/search/SpellSearchResultEntry.tsx";

interface SearchResultEntryProps {
    result: SearchResult;
    query: string;
    onSelect: () => void;
    selecting?: boolean;
}

function Metadata({items}: { items: ReactNode[] }) {
    return (
        <div className="mt-0.5 flex min-w-0 flex-wrap items-center gap-x-1.5 text-xs text-muted-foreground">
            {items.map((item, index) =>
                <span key={index} className="inline-flex items-center gap-1.5">
                    {index > 0 && <span aria-hidden="true" className="text-border">•</span>}
                    {item}
                </span>)}
        </div>
    );
}

export default function SearchResultEntry({result, query, onSelect, selecting}: SearchResultEntryProps) {
    const {showSpell, showCharacter} = useModal();
    const {activeEncounter} = useActiveEncounter();
    const [adding, setAdding] = useState(false);
    const [added, setAdded] = useState(false);
    const [addError, setAddError] = useState(false);
    const addedTimer = useRef<number | undefined>(undefined);

    useEffect(() => () => window.clearTimeout(addedTimer.current), []);

    const addCombatant = async () => {
        if (!activeEncounter || adding) return;
        setAdding(true);
        setAdded(false);
        setAddError(false);
        window.clearTimeout(addedTimer.current);
        try {
            await addCharacterToEncounter(activeEncounter.id, result.id);
            setAdded(true);
            addedTimer.current = window.setTimeout(() => setAdded(false), 1500);
        } catch {
            setAddError(true);
        } finally {
            setAdding(false);
        }
    };

    if (result.type === "SPELL") return <SpellSearchResultEntry
        spell={result}
        query={query}
        selecting={selecting}
        actionLabel={`${result.name} öffnen`}
        onSelect={() => {
            showSpell(result.id);
            onSelect();
        }}
    />;

    const color = getStyleColor(result);
    const challengeRating = formatChallengeRating(result);
    const metadata: ReactNode[] = [
        <span className={color.text}>{result.hostile ? "Feindlich" : "Freundlich"}</span>,
        challengeRating && `HG ${challengeRating}`,
        `${result.hitPoints} TP`,
    ].filter(Boolean) as ReactNode[];

    return (
        <div className={cn(
            "group flex items-center gap-1 border-l-4 bg-popover transition-colors hover:bg-accent/70",
            result.hostile ? "border-l-red-500" : "border-l-emerald-500",
        )}>
            <button
                type="button"
                className="flex min-w-0 grow cursor-pointer items-center gap-3 rounded-md px-2 py-1 text-left outline-none focus-visible:ring-3 focus-visible:ring-ring/40"
                onClick={() => {
                    showCharacter(result.id);
                    onSelect();
                }}
                aria-label={`${result.name} öffnen`}
            >
                <div className={cn(
                    "flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-sm border bg-white",
                    color.border,
                )}>
                    {result.image
                        ? <img
                            draggable="false" alt="" className="size-full select-none object-contain"
                            src={srcUrl(result.image)}
                        />
                        : <UserRoundIcon className="size-5"/>}
                </div>
                <div className="min-w-0 grow">
                    <div className="truncate font-semibold text-foreground">
                        <HighlightedSearchText text={result.name} query={query}/>
                    </div>
                    <Metadata items={metadata}/>
                </div>
            </button>

            {activeEncounter &&
                <Button
                    type="button"
                    size="icon-sm"
                    variant={addError ? "destructive" : "primary"}
                    className={cn("mr-1 size-8", added && "text-success")}
                    disabled={adding}
                    title={addError
                        ? `Konnte nicht zu ${activeEncounter.name} hinzugefügt werden`
                        : added
                            ? `Zu ${activeEncounter.name} hinzugefügt`
                            : `Zu ${activeEncounter.name} hinzufügen`}
                    aria-label={addError
                        ? `Konnte ${result.name} nicht zu ${activeEncounter.name} hinzufügen`
                        : `${result.name} zu ${activeEncounter.name} hinzufügen`}
                    onClick={() => void addCombatant()}
                >
                    {adding
                        ? <Loader2Icon className="animate-spin"/>
                        : addError
                            ? <CircleAlertIcon/>
                            : added ? <CheckIcon/> : <PlusIcon/>}
                </Button>}
        </div>
    );
}
